// PRIMERO de todo, antes que supabase (que borra el hash de la URL nada más
// procesarlo) y antes que React — ver comentario en el propio módulo.
import './lib/recoveryFlag.js'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import { captureLaunchParams, nativeAppLabel } from './lib/nativeApp.js'

// App de Android (TWA): guarda ?nativeApp/appVersion/appBuild y los quita de la URL
captureLaunchParams()
import { initAnalytics } from './lib/analytics.js'
import { isBusy } from './lib/busy.js'
import { installEscapeToClose } from './lib/a11y.js'
import './styles/app.css'
import './i18n/index.js'
import App from './app/App.jsx'

// Tras un despliegue, los ficheros de la app cambian de nombre. Una pestaña
// abierta de antes puede pedir uno de la versión vieja, que ya no existe, y
// la pantalla o el botón que lo necesitaba no responde hasta recargar. Se
// recarga sola para coger la versión nueva — una vez cada 30 s como mucho,
// para no entrar en bucle si el fallo es otro (p. ej. sin conexión).
function reloadOnStaleBundle() {
  try {
    const KEY = 'nivela_stale_reload_at'
    const last = Number(sessionStorage.getItem(KEY) || 0)
    if (Date.now() - last < 30000) return
    sessionStorage.setItem(KEY, String(Date.now()))
  } catch { return }   // sin almacenamiento no hay forma de evitar el bucle: mejor no recargar
  window.location.reload()
}
const STALE_BUNDLE_RE = /Importing a module script failed|Failed to fetch dynamically imported module|error loading dynamically imported module|Unable to preload CSS/i
// Sin preventDefault(): con él Vite da la importación por buena pero sin
// contenido, y React.lazy fallaba con "undefined is not an object (evaluating
// 'A._result.default')" (NIVELA-AI-K, 2026-10-16), sin recargar si ya lo había
// hecho en los últimos 30 s. Así el error sigue su curso (lo filtra beforeSend).
window.addEventListener('vite:preloadError', () => { reloadOnStaleBundle() })
window.addEventListener('unhandledrejection', (e) => {
  if (STALE_BUNDLE_RE.test(String(e.reason?.message || e.reason || ''))) reloadOnStaleBundle()
})
window.addEventListener('error', (e) => {
  if (STALE_BUNDLE_RE.test(String(e.message || ''))) reloadOnStaleBundle()
})

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.1,
    enabled: import.meta.env.PROD,
    beforeSend(event) {
      const msg = event.exception?.values?.[0]?.value ?? ''
      const type = event.exception?.values?.[0]?.type ?? ''
      if (msg.includes('Failed to update a ServiceWorker')) return null
      if (msg.includes('sw-v5.js')) return null
      if (msg.includes('sw-v4.js')) return null   // service worker antiguo, ya retirado: fallo puntual de red al comprobarlo
      // El nombre del error va en `type`, no en el mensaje ("newestWorker is null")
      if (type === 'InvalidStateError' || msg.includes('InvalidStateError')) return null
      if (msg.includes('EmptyRanges')) return null
      if (msg.includes('LockManager lock') && msg.includes('timed out')) return null
      // Fichero de una versión anterior que ya no existe: la app se recarga sola
      // (reloadOnStaleBundle, arriba); no es un error que haya que atender.
      if (STALE_BUNDLE_RE.test(msg)) return null
      // "Object Not Found Matching Id:N, MethodName:update, ParamCount:4": no es
      // de la app. Lo lanza el navegador automático con el que Microsoft
      // (Outlook/Defender) abre los enlaces de los correos para revisarlos;
      // visto el 2026-10-12 desde IPs de Azure, en /login y sin usuario.
      if (/Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/.test(msg)) return null
      const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? []
      // "AbortError: The operation was aborted" sin pila: una operación que el
      // navegador cancela (p. ej. peticiones de PostHog/Supabase al pasar la app
      // a segundo plano en iPhone). No viene de nuestro código ni afecta al
      // usuario — NIVELA-AI-M, 2026-10-18.
      if ((type === 'AbortError' || msg.includes('AbortError')) && !frames.length) return null
      // Ignore errors from browser extensions (stack points to undefined/null script)
      if (frames.length && frames.every(f => !f.filename || f.filename === 'undefined')) return null
      return event
    },
  })
  // Desde qué se usa la app: web o la app nativa con su versión (lib/nativeApp.js)
  Sentry.setTag('app', nativeAppLabel())
}

// Se carga cuando la página ya ha pintado (lib/analytics.js)
if (import.meta.env.PROD) {
  initAnalytics(import.meta.env.VITE_POSTHOG_KEY, import.meta.env.VITE_POSTHOG_HOST)
}
import { registerSW } from "virtual:pwa-register";
// Cuando un Service Worker nuevo toma el control (skipWaiting+clientsClaim),
// la página YA cargada sigue siendo la vieja hasta que se recarga. Sin esto,
// la app (sobre todo instalada como PWA) se quedaba mostrando una versión
// antigua indefinidamente aunque el SW nuevo ya estuviera activo — visto en
// producción 2026-07-30. El guard evita un bucle de recargas.
// Solo si ya había un SW controlando la página: en la PRIMERA visita el SW
// toma el control sin que haya versión vieja que sustituir, y recargaba la
// página a mitad de carga (o con el formulario de alta a medio rellenar) —
// visto 2026-10-17 midiendo la carga del login.
// Recarga por versión nueva, pero nunca a mitad de grabar o subir un vídeo
// (lib/busy.js): se espera a que termine y entonces se recarga.
let reloadPending = false
function reloadForUpdate() {
  if (!isBusy()) { window.location.reload(); return }
  if (reloadPending) return
  reloadPending = true
  const id = setInterval(() => {
    if (!isBusy()) { clearInterval(id); window.location.reload() }
  }, 3000)
}
if ('serviceWorker' in navigator) {
  let reloading = false
  const hadController = !!navigator.serviceWorker.controller
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloading || !hadController) return
    reloading = true
    reloadForUpdate()
  })
}
registerSW({
  immediate: true,
  onRegisteredSW(swUrl, r) {
    // Chequeo periódico: iOS no recarga el SW automáticamente
    // Safari rechaza update() si el registro se ha quedado sin worker
    // ("newestWorker is null"); se reintenta al minuto siguiente.
    if (r) setInterval(() => r.update().catch(() => {}), 60 * 1000)
  },
  onNeedRefresh() {
    // Nueva versión disponible → recargar (funciona en iOS standalone)
    reloadForUpdate()
  },
});

// Escape cierra la ventana emergente de encima (accesibilidad con teclado)
installEscapeToClose()

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <App />
  </StrictMode>,
)
